import {
    correlationIdHeader,
    genCorrelationId,
    UnauthenticatedError,
} from "@sp-crm/core";
import type { TypedDocumentString } from "generated/graphql";
import { GraphQLFormattedError } from "graphql";
import { extraRequestHeaders } from "util/browser";
import { setErrorTrackingContext } from "util/error-tracking";
import { recordExceptionStep } from "util/exception-sink";
import { ClientHttpError } from "util/http";
import { checkVersionHeader } from "util/version-check";

export class ClientGraphqlError extends Error {
    public readonly skipReporting: boolean;
    constructor(
        public readonly correlationId: string,
        public readonly graphqlErrors: readonly GraphQLFormattedError[],
    ) {
        super(ClientGraphqlError.getFirstErrorMessage(graphqlErrors));

        this.hasErrorType = (errorType: string) => {
            return this.graphqlErrors.some(
                e => e.extensions && e.extensions["errorType"] === errorType,
            );
        };

        this.skipReporting = this.graphqlErrors.some(
            e =>
                e.extensions &&
                "skipReporting" in e.extensions &&
                e.extensions["skipReporting"],
        );
        Object.setPrototypeOf(this, new.target.prototype);
    }

    public hasErrorType = (errorType: string): boolean => {
        return this.graphqlErrors.some(
            e => e.extensions && e.extensions["errorType"] === errorType,
        );
    };

    public hasMessageContaining = (needle: string): boolean => {
        return this.graphqlErrors.some(e => e.message.includes(needle));
    };

    public messageForErrorTypes = (errorTypes: readonly string[]): string | undefined => {
        return this.graphqlErrors.find(e => {
            const errorType = e.extensions?.["errorType"];
            return typeof errorType === "string" && errorTypes.includes(errorType);
        })?.message;
    };

    static getFirstErrorMessage(graphqlErrors: readonly GraphQLFormattedError[]) {
        if (graphqlErrors && graphqlErrors.length > 0) {
            return graphqlErrors[0]?.message;
        }

        return "An unknown error occurred.";
    }
}

export class GatewayTimeoutError extends Error {
    constructor(public readonly correlationId: string) {
        super(
            "This took longer than expected and didn't complete. Please try again, or contact support if this keeps happening.",
        );
        this.name = "GatewayTimeoutError";
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

export class ClientGraphqlNetworkError extends Error {
    constructor(public readonly originalError: Error) {
        super(`Network error: ${originalError?.message || "Fetch error"}`);
        this.name = "ClientGraphqlNetworkError";
    }
}

type HttpResponseBody =
    | {
          type: "json";
          body: Record<string, unknown>;
      }
    | {
          type: "text";
          body: string;
      };

export const readResponseText = async (res: Response): Promise<string> => {
    try {
        return await res.text();
    } catch (e) {
        throw new ClientGraphqlNetworkError(
            e instanceof Error ? e : new Error(String(e)),
        );
    }
};

export const readResponseJson = async (res: Response): Promise<unknown> => {
    try {
        return await res.json();
    } catch (e) {
        throw new ClientGraphqlNetworkError(
            e instanceof Error ? e : new Error(String(e)),
        );
    }
};

const parseResponseBody = async (res: Response): Promise<HttpResponseBody> => {
    const text = await readResponseText(res);
    try {
        return { type: "json", body: JSON.parse(text) };
    } catch (e) {
        return { type: "text", body: text };
    }
};

const makeErrorsArray = (json: Record<string, unknown>) => {
    if ("errors" in json && Array.isArray(json.errors)) {
        return json.errors;
    }
    return [
        {
            message: "An unknown error occurred.",
            locations: [],
        },
    ];
};

const throwGraphqlError = async (res: Response): Promise<never> => {
    if (res.status === 401) {
        throw new UnauthenticatedError();
    }

    if (res.status === 504) {
        throw new GatewayTimeoutError(res.headers.get(correlationIdHeader) || "");
    }

    const responseBody = await parseResponseBody(res);

    if (responseBody.type === "json") {
        const errors = makeErrorsArray(responseBody.body);

        throw new ClientGraphqlError(res.headers.get(correlationIdHeader) || "", errors);
    } else {
        throw new ClientHttpError(
            res.headers.get(correlationIdHeader) || "",
            res.status,
            res.statusText,
            responseBody.body,
            "GraphQL",
        );
    }
};

interface GraphqlFetcherContext {
    signal?: AbortSignal;
}

const isAbortError = (e: unknown): boolean =>
    e instanceof DOMException && e.name === "AbortError";

export function graphqlFetcher<TData, TVariables>(
    query: string | TypedDocumentString<unknown, unknown>,
    variables?: TVariables,
) {
    return async (context?: GraphqlFetcherContext): Promise<TData> => {
        let res: Response;

        const correlationId = genCorrelationId("client");

        try {
            res = await fetch("/api/graphql" as string, {
                method: "POST",
                ...{
                    headers: {
                        ...extraRequestHeaders(),
                        "content-type": "application/json",
                        [correlationIdHeader]: correlationId,
                    },
                },
                body: JSON.stringify({ query: query.toString(), variables }),
                signal: context?.signal ?? null,
            });
        } catch (e) {
            if (isAbortError(e)) {
                throw e;
            }
            throw new ClientGraphqlNetworkError(
                e instanceof Error ? e : new Error(String(e)),
            );
        }

        const setGraphqlErrorContext = () => {
            setErrorTrackingContext({
                query,
                correlationId,
            });
            recordExceptionStep("graphql-error", {
                correlation_id: correlationId,
                status: res.status,
            });
        };

        checkVersionHeader(res.headers);

        if (!res.ok) {
            setGraphqlErrorContext();
            await throwGraphqlError(res);
        }

        const json = (await readResponseJson(res)) as {
            data: TData;
            errors?: readonly GraphQLFormattedError[];
        };

        if (json.errors) {
            setGraphqlErrorContext();
            throw new ClientGraphqlError(correlationId, json.errors);
        }

        return json.data;
    };
}
